<template>
  <div class="ep-page">

    <!-- ── Leave Balances ─────────────────────────────────── -->
    <div>
      <div class="flex items-center justify-between gap-3 mb-3">
        <h2 class="ep-section-title"><Calendar class="w-4 h-4 text-indigo-400" /> {{ t('empLeave.yourBalance') }}</h2>
        <button @click="showNewRequestModal = true" class="ep-btn ep-btn-primary ep-btn-sm shrink-0">
          <Plus class="w-4 h-4" /> {{ t('empLeave.newRequest') }}
        </button>
      </div>
      <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
        <div
          v-for="balance in leaveBalances"
          :key="balance.leave_type_id || balance.id"
          class="ep-card ep-card-hover p-5 relative overflow-hidden group"
        >
          <!-- accent tint -->
          <div class="absolute inset-x-0 top-0 h-1 rounded-t-2xl"
               :style="{ background: getLeaveTypeColor(balance.leave_type) }"></div>

          <!-- Header -->
          <div class="flex items-center justify-between mb-4">
            <div class="ep-stat-icon transition-transform duration-200 group-hover:scale-105"
                 :style="{ background: `${getLeaveTypeColor(balance.leave_type)}1f`, color: getLeaveTypeColor(balance.leave_type) }">
              <component :is="getLeaveTypeIcon(balance.leave_type)" class="w-5 h-5" />
            </div>
            <div class="text-right">
              <p class="text-2xl font-bold text-slate-100 tabular-nums leading-none">
                {{ balance.remaining_days || balance.remaining || 0 }}
              </p>
              <p class="text-[11px] text-slate-500 font-medium mt-1">{{ balance.unit || t('empLeave.days') }} {{ t('empLeave.daysLeft') }}</p>
            </div>
          </div>

          <!-- Name -->
          <h3 class="text-base font-semibold text-slate-200">
            {{ formatLeaveTypeName(balance.name || balance.leave_type || balance.type) }}
          </h3>
          <p class="text-xs text-slate-500 mt-0.5">
            {{ balance.used_days || balance.used || 0 }} {{ t('empLeave.usedOf') }} {{ balance.total_days || balance.total || 0 }} {{ t('empLeave.total') }}
          </p>

          <!-- Included leave types (banks) -->
          <div v-if="balance.is_bank && balance.leave_types && balance.leave_types.length > 0" class="mt-2.5">
            <div class="flex flex-wrap gap-1.5">
              <span v-for="lt in balance.leave_types" :key="lt.id"
                    class="px-2 py-0.5 text-[10px] font-medium rounded-full"
                    :style="{ border: `1px solid ${lt.color || '#64748b'}55`, color: lt.color || '#94a3b8', background: `${lt.color || '#64748b'}12` }">
                {{ lt.name }}
              </span>
            </div>
          </div>

          <!-- Progress -->
          <div class="mt-4">
            <div class="flex items-center justify-between text-[11px] text-slate-500 mb-1.5">
              <span>{{ t('empLeave.usage') }}</span>
              <span class="tabular-nums">{{ getUsagePercentage(balance) }}{{ t('empLeave.percentUsed') }}</span>
            </div>
            <div class="ep-progress">
              <div class="h-full rounded-full transition-all duration-700 ease-out"
                   :style="{
                     width: `${Math.min(getUsagePercentage(balance), 100)}%`,
                     background: getUsagePercentage(balance) > 80 ? '#f87171'
                              : getUsagePercentage(balance) > 60 ? '#fbbf24'
                              : getLeaveTypeColor(balance.leave_type)
                   }"></div>
            </div>
          </div>

          <!-- CTA -->
          <button
            @click="quickRequest(balance.leave_type)"
            :disabled="(balance.remaining_days || balance.remaining || 0) <= 0"
            class="ep-btn ep-btn-secondary w-full mt-4 !text-xs"
          >
            {{ (balance.remaining_days || balance.remaining || 0) > 0
               ? `Request ${formatLeaveTypeName(balance.name || balance.leave_type || balance.type)}`
               : t('empLeave.noBalanceAvailable') }}
          </button>
        </div>

        <!-- New Request card -->
        <button
          @click="showNewRequestModal = true"
          class="group rounded-2xl p-5 min-h-[220px] flex flex-col items-center justify-center text-center transition-all duration-200 hover:bg-indigo-500/[0.06]"
          style="border: 2px dashed rgba(255,255,255,0.1)"
        >
          <div class="w-14 h-14 rounded-2xl flex items-center justify-center mb-3 transition-all duration-200 group-hover:scale-105"
               style="background: rgba(99,102,241,0.1); border: 1px solid rgba(99,102,241,0.25)">
            <Plus class="w-7 h-7 text-indigo-400" />
          </div>
          <h3 class="text-base font-semibold text-slate-300 group-hover:text-indigo-300 transition-colors">
            {{ t('empLeave.newLeaveRequest') }}
          </h3>
          <p class="text-xs text-slate-500 mt-1.5 max-w-[16rem]">
            {{ t('empLeave.newLeaveSubtitle') }}
          </p>
        </button>
      </div>
    </div>

    <!-- ── Quick Stats ────────────────────────────────────── -->
    <div class="grid grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-4">
      <div class="ep-card ep-card-hover p-4 sm:p-5 flex items-center gap-3.5">
        <div class="ep-stat-icon ep-stat-amber"><Clock class="w-5 h-5" /></div>
        <div>
          <p class="text-xs font-medium text-slate-500">{{ t('empLeave.pending') }}</p>
          <p class="text-xl font-bold text-slate-100 tabular-nums">{{ pendingCount }}</p>
        </div>
      </div>
      <div class="ep-card ep-card-hover p-4 sm:p-5 flex items-center gap-3.5">
        <div class="ep-stat-icon ep-stat-emerald"><CheckCircle class="w-5 h-5" /></div>
        <div>
          <p class="text-xs font-medium text-slate-500">{{ t('empLeave.approved') }}</p>
          <p class="text-xl font-bold text-slate-100 tabular-nums">{{ approvedCount }}</p>
        </div>
      </div>
      <div class="ep-card ep-card-hover p-4 sm:p-5 flex items-center gap-3.5">
        <div class="ep-stat-icon ep-stat-indigo"><Calendar class="w-5 h-5" /></div>
        <div>
          <p class="text-xs font-medium text-slate-500">{{ t('empLeave.thisMonth') }}</p>
          <p class="text-xl font-bold text-slate-100 tabular-nums">{{ thisMonthCount }}</p>
        </div>
      </div>
      <div class="ep-card ep-card-hover p-4 sm:p-5 flex items-center gap-3.5">
        <div class="ep-stat-icon ep-stat-violet"><TrendingUp class="w-5 h-5" /></div>
        <div>
          <p class="text-xs font-medium text-slate-500">{{ t('empLeave.totalRequests') }}</p>
          <p class="text-xl font-bold text-slate-100 tabular-nums">{{ leaveRequests.length }}</p>
        </div>
      </div>
    </div>

    <!-- ── Request History ────────────────────────────────── -->
    <div class="ep-card overflow-hidden">
      <!-- Filter bar -->
      <div class="p-5" style="border-bottom: 1px solid rgba(255,255,255,0.06)">
        <div class="flex items-center justify-between mb-4">
          <h3 class="ep-section-title"><FileText class="w-4 h-4 text-indigo-400" /> {{ t('empLeave.leaveHistory') }}</h3>
          <span class="text-xs text-slate-500">{{ filteredRequests.length }} {{ t('empLeave.requests') }}</span>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <label class="ep-label">{{ t('common.status') }}</label>
            <select v-model="filters.status" @change="applyFilters" class="ep-select">
              <option value="">{{ t('empLeave.allStatus') }}</option>
              <option value="pending">{{ t('empLeave.pending') }}</option>
              <option value="approved">{{ t('empLeave.approved') }}</option>
              <option value="rejected">{{ t('common.rejected') }}</option>
              <option value="cancelled">{{ t('leave.cancelled') }}</option>
            </select>
          </div>
          <div>
            <label class="ep-label">{{ t('common.type') }}</label>
            <select v-model="filters.type" @change="applyFilters" class="ep-select">
              <option value="">{{ t('empLeave.allTypes') }}</option>
              <option value="annual">{{ t('empLeave.annual') }}</option>
              <option value="sick">{{ t('empLeave.sick') }}</option>
              <option value="personal">{{ t('empLeave.personal') }}</option>
            </select>
          </div>
          <div>
            <label class="ep-label">Period</label>
            <select v-model="filters.dateRange" @change="applyFilters" class="ep-select">
              <option value="">{{ t('empLeave.allTime') }}</option>
              <option value="month">{{ t('empLeave.thisMonth') }}</option>
              <option value="quarter">{{ t('empHistory.thisQuarter') }}</option>
              <option value="year">This Year</option>
            </select>
          </div>
          <div class="flex items-end">
            <button @click="resetFilters" class="ep-btn ep-btn-secondary w-full">{{ t('empLeave.reset') }}</button>
          </div>
        </div>
      </div>

      <!-- List -->
      <div class="p-5">
        <!-- Empty -->
        <div v-if="filteredRequests.length === 0" class="ep-empty">
          <Calendar class="w-12 h-12" />
          <p class="text-sm font-medium text-slate-400">{{ t('empLeave.noLeaveFound') }}</p>
          <p class="text-xs text-slate-600 mt-1">
            {{ leaveRequests.length === 0 ? t('empLeave.noLeaveYet') : t('common.noResults') }}
          </p>
          <button @click="showNewRequestModal = true" class="ep-btn ep-btn-primary ep-btn-sm mt-4">
            <Plus class="w-3.5 h-3.5" /> {{ t('empLeave.submitFirst') }}
          </button>
        </div>

        <!-- Cards -->
        <div v-else class="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div
            v-for="request in paginatedRequests"
            :key="request.id"
            class="rounded-2xl p-5 transition-all duration-200 hover:bg-white/[0.03]"
            style="background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.06)"
          >
            <!-- Card header -->
            <div class="flex items-center justify-between gap-3 mb-3.5">
              <div class="flex items-center gap-3 min-w-0">
                <div class="ep-stat-icon !w-10 !h-10 !rounded-xl shrink-0"
                     :style="{ background: `${getLeaveTypeColor(request.leave_type_code || request.leave_type)}1f`,
                               color: getLeaveTypeColor(request.leave_type_code || request.leave_type) }">
                  <component :is="getLeaveTypeIcon(request.leave_type_code || request.leave_type)" class="w-[1.125rem] h-[1.125rem]" />
                </div>
                <div class="min-w-0">
                  <h4 class="text-sm font-semibold text-slate-200 truncate">
                    {{ formatLeaveTypeName(request.leave_type_name || request.leave_type) }}
                  </h4>
                  <p class="text-xs text-slate-500">{{ formatDateRange(request) }}</p>
                </div>
              </div>
              <span :class="getStatusBadgeClass(request.status)">
                <span class="ep-dot"></span>{{ formatStatus(request.status) }}
              </span>
            </div>

            <!-- Details -->
            <div class="space-y-2 mb-4">
              <div class="flex items-center text-xs text-slate-500">
                <Clock class="w-3.5 h-3.5 mr-1.5" />
                {{ request.days }} {{ request.days === 1 ? t('empLeave.day') : t('empLeave.days') }}
              </div>
              <p class="text-sm text-slate-400 line-clamp-2">{{ request.reason || t('empLeave.noReasonProvided') }}</p>
              <p class="text-[11px] text-slate-600">{{ t('empLeave.submitted') }} {{ formatRelativeDate(request.created_at) }}</p>
            </div>

            <!-- Footer -->
            <div class="flex items-center justify-between pt-3.5" style="border-top: 1px solid rgba(255,255,255,0.06)">
              <button @click="viewRequest(request)"
                      class="text-xs font-medium text-indigo-400 hover:text-indigo-300 transition-colors">
                {{ t('empLeave.viewDetails') }}
              </button>
              <button v-if="canCancel(request)" @click="cancelRequest(request)"
                      class="ep-btn ep-btn-danger ep-btn-sm !text-xs">
                {{ t('empLeave.cancel') }}
              </button>
            </div>
          </div>
        </div>

        <!-- Pagination -->
        <div v-if="totalPages > 1" class="mt-6 flex items-center justify-center">
          <div class="flex items-center gap-1 rounded-xl p-1.5"
               style="background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.06)">
            <button @click="previousPage" :disabled="currentPage <= 1"
                    class="ep-btn ep-btn-ghost ep-btn-sm !px-2">
              <ChevronLeft class="w-4 h-4" />
            </button>
            <button v-for="page in visiblePages" :key="page"
                    @click="goToPage(page)"
                    :disabled="page === '...'"
                    :class="['ep-btn ep-btn-sm min-w-[2.1rem]',
                             page === currentPage ? 'ep-btn-primary' : 'ep-btn-ghost']">
              {{ page }}
            </button>
            <button @click="nextPage" :disabled="currentPage >= totalPages"
                    class="ep-btn ep-btn-ghost ep-btn-sm !px-2">
              <ChevronRight class="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ── New Leave Request Modal ────────────────────────── -->
    <LeaveRequestModal
      v-if="showNewRequestModal"
      :leaveTypes="enhancedLeaveTypes"
      :preSelectedType="preSelectedLeaveType"
      @close="closeNewRequestModal"
      @submit="handleNewRequest"
    />

    <!-- ── View Request Modal ─────────────────────────────── -->
    <div v-if="showViewModal" class="ep-modal-backdrop" @click.self="closeViewModal">
      <div class="ep-modal max-w-2xl">
        <div class="flex items-center justify-between px-6 py-4"
             style="border-bottom: 1px solid rgba(255,255,255,0.07)">
          <h3 class="text-base font-semibold text-slate-100">{{ t('empLeave.leaveDetails') }}</h3>
          <button @click="closeViewModal"
                  class="p-1.5 rounded-lg text-slate-500 hover:text-slate-300 hover:bg-white/5 transition-colors">
            <X class="w-5 h-5" />
          </button>
        </div>

        <div v-if="selectedRequest" class="p-6">
          <!-- Header -->
          <div class="flex items-center gap-4 mb-6">
            <div class="ep-stat-icon !w-14 !h-14 shrink-0"
                 :style="{ background: `${getLeaveTypeColor(selectedRequest.leave_type)}1f`,
                           color: getLeaveTypeColor(selectedRequest.leave_type) }">
              <component :is="getLeaveTypeIcon(selectedRequest.leave_type)" class="w-7 h-7" />
            </div>
            <div>
              <h4 class="text-base font-semibold text-slate-100">
                {{ formatLeaveTypeName(selectedRequest.leave_type) }}
              </h4>
              <span :class="getStatusBadgeClass(selectedRequest.status)" class="mt-1">
                <span class="ep-dot"></span>{{ formatStatus(selectedRequest.status) }}
              </span>
            </div>
          </div>

          <!-- Detail grid -->
          <div class="grid grid-cols-2 gap-x-6 gap-y-4 mb-6">
            <div>
              <p class="ep-label">{{ t('empLeave.startDate') }}</p>
              <p class="text-sm text-slate-200">{{ formatDate(selectedRequest.start_date) }}</p>
            </div>
            <div>
              <p class="ep-label">{{ t('empLeave.endDate') }}</p>
              <p class="text-sm text-slate-200">{{ formatDate(selectedRequest.end_date) }}</p>
            </div>
            <div>
              <p class="ep-label">{{ t('empLeave.duration') }}</p>
              <p class="text-sm text-slate-200">
                {{ selectedRequest.days }} {{ selectedRequest.days === 1 ? t('empLeave.day') : t('empLeave.days') }}
              </p>
            </div>
          </div>

          <!-- Reason -->
          <div class="mb-6">
            <p class="ep-label">{{ t('empLeave.reason') }}</p>
            <div class="rounded-xl p-3.5"
                 style="background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.06)">
              <p class="text-sm text-slate-300 whitespace-pre-wrap">{{ selectedRequest.reason }}</p>
            </div>
          </div>

          <!-- Review info -->
          <div v-if="selectedRequest.status !== 'pending'" class="pt-5 mb-6"
               style="border-top: 1px solid rgba(255,255,255,0.07)">
            <h5 class="text-sm font-semibold text-slate-200 mb-4">{{ t('empLeave.reviewInfo') }}</h5>
            <div class="grid grid-cols-2 gap-6">
              <div>
                <p class="ep-label">{{ t('empLeave.reviewedBy') }}</p>
                <p class="text-sm text-slate-200">{{ selectedRequest.reviewed_by_name || t('common.system') }}</p>
              </div>
              <div>
                <p class="ep-label">{{ t('empLeave.reviewDate') }}</p>
                <p class="text-sm text-slate-200">{{ formatDateTime(selectedRequest.reviewed_at) }}</p>
              </div>
            </div>
            <div v-if="selectedRequest.review_notes" class="mt-4">
              <p class="ep-label">{{ t('empLeave.reviewNotes') }}</p>
              <div class="rounded-xl p-3.5"
                   style="background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.06)">
                <p class="text-sm text-slate-300 whitespace-pre-wrap">{{ selectedRequest.review_notes }}</p>
              </div>
            </div>
          </div>

          <!-- Actions -->
          <div class="flex gap-2.5 pt-5" style="border-top: 1px solid rgba(255,255,255,0.07)">
            <button v-if="canCancel(selectedRequest)" @click="cancelRequest(selectedRequest)"
                    class="ep-btn ep-btn-danger flex-1">
              {{ t('empLeave.cancelRequest') }}
            </button>
            <button @click="closeViewModal" class="ep-btn ep-btn-secondary flex-1">{{ t('empLeave.close') }}</button>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Toasts ─────────────────────────────────────────── -->
    <div v-if="successMessage"
         class="fixed bottom-24 lg:bottom-4 right-4 z-50 flex items-center gap-2.5 px-4 py-3 rounded-xl shadow-2xl toast-pop"
         style="background: rgba(11,16,31,0.97); border: 1px solid rgba(52,211,153,0.35)">
      <CheckCircle class="w-4 h-4 text-emerald-400 shrink-0" />
      <span class="text-sm text-slate-200">{{ successMessage }}</span>
    </div>

    <div v-if="errorMessage"
         class="fixed bottom-24 lg:bottom-4 right-4 z-50 flex items-center gap-2.5 px-4 py-3 rounded-xl shadow-2xl toast-pop"
         style="background: rgba(11,16,31,0.97); border: 1px solid rgba(248,113,113,0.35)">
      <AlertCircle class="w-4 h-4 text-red-400 shrink-0" />
      <span class="text-sm text-slate-200">{{ errorMessage }}</span>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  Plus,
  Calendar,
  Heart,
  User,
  Clock,
  FileText,
  ChevronLeft,
  ChevronRight,
  X,
  CheckCircle,
  AlertCircle,
  TrendingUp,
  Plane,
  Briefcase
} from 'lucide-vue-next';
import httpClient from '@/services/httpClient.js';
import LeaveRequestModal from '@/components/leave/LeaveRequestModal.vue';

const { t } = useI18n();

// Reactive data
const leaveRequests = ref([]);
const leaveBalances = ref([]);
const leaveTypes = ref([]);
const successMessage = ref('');
const errorMessage = ref('');
const showNewRequestModal = ref(false);
const showViewModal = ref(false);
const selectedRequest = ref(null);
const preSelectedLeaveType = ref(null);

const filters = reactive({
  status: '',
  type: '',
  dateRange: '',
});

const currentPage = ref(1);
const requestsPerPage = ref(8);

// Computed properties
const filteredRequests = computed(() => {
  let filtered = leaveRequests.value;

  if (filters.status) {
    filtered = filtered.filter(request => request.status === filters.status);
  }

  if (filters.type) {
    filtered = filtered.filter(request => request.leave_type === filters.type);
  }

  if (filters.dateRange) {
    const now = new Date();
    let startDate;

    switch (filters.dateRange) {
      case 'month':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case 'quarter':
        const quarter = Math.floor(now.getMonth() / 3);
        startDate = new Date(now.getFullYear(), quarter * 3, 1);
        break;
      case 'year':
        startDate = new Date(now.getFullYear(), 0, 1);
        break;
    }

    if (startDate) {
      filtered = filtered.filter(
        request => new Date(request.start_date) >= startDate
      );
    }
  }

  return filtered.sort(
    (a, b) => new Date(b.created_at) - new Date(a.created_at)
  );
});

const totalPages = computed(() =>
  Math.ceil(filteredRequests.value.length / requestsPerPage.value)
);

const paginatedRequests = computed(() => {
  const start = (currentPage.value - 1) * requestsPerPage.value;
  const end = start + requestsPerPage.value;
  return filteredRequests.value.slice(start, end);
});

const visiblePages = computed(() => {
  const pages = [];
  const total = totalPages.value;
  const current = currentPage.value;

  if (total <= 7) {
    for (let i = 1; i <= total; i++) {
      pages.push(i);
    }
  } else {
    if (current <= 4) {
      for (let i = 1; i <= 5; i++) pages.push(i);
      pages.push('...');
      pages.push(total);
    } else if (current >= total - 3) {
      pages.push(1);
      pages.push('...');
      for (let i = total - 4; i <= total; i++) pages.push(i);
    } else {
      pages.push(1);
      pages.push('...');
      for (let i = current - 1; i <= current + 1; i++) pages.push(i);
      pages.push('...');
      pages.push(total);
    }
  }

  return pages;
});

// Stats computed
const pendingCount = computed(() => 
  leaveRequests.value.filter(r => r.status === 'pending').length
);

const approvedCount = computed(() => 
  leaveRequests.value.filter(r => r.status === 'approved').length
);

const thisMonthCount = computed(() => {
  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  return leaveRequests.value.filter(r => 
    new Date(r.start_date) >= startOfMonth
  ).length;
});

// Short leave is handled by the attendance clock system, not this page
const SHORT_LEAVE_CODES = ['short', 'sh', 'short_leave', 'shortleave'];

const isShortLeaveCode = (value) =>
  typeof value === 'string' && SHORT_LEAVE_CODES.includes(value.toLowerCase());

const isShortLeaveRecord = (record) =>
  record.is_short_leave === 1 || record.is_short_leave === true ||
  isShortLeaveCode(record.leave_type) || isShortLeaveCode(record.leave_type_code) ||
  isShortLeaveCode(record.type);

// Methods
const getLeaveTypeIcon = (leaveType) => {
  if (!leaveType) return Briefcase;
  
  const normalizedType = leaveType.toLowerCase();
  
  const icons = {
    // Standard types
    'annual': Plane,
    'sick': Heart,
    'personal': User,
    'emergency': AlertCircle,
    'maternity': Heart,
    'paternity': User,
    'casual': Calendar,
    'vacation': Plane,
    'bereavement': Heart,
    'holiday': Calendar,
    
    // Database codes
    'al': Plane,      // Annual Leave
    'sl': Heart,      // Sick Leave
    'pl': User,       // Personal Leave
    'el': AlertCircle, // Emergency Leave
    'ml': Heart,      // Maternity Leave
    'ptl': User,      // Paternity Leave
    'cl': Calendar,   // Casual Leave
    'vl': Plane,      // Vacation Leave
    'bl': Heart,      // Bereavement Leave
    'hl': Calendar    // Holiday Leave
  };
  
  return icons[normalizedType] || Briefcase;
};

const getLeaveTypeColor = (leaveType) => {
  if (!leaveType) return '#6B7280';
  
  const normalizedType = leaveType.toLowerCase();
  
  const colors = {
    // Standard types
    'annual': '#3B82F6',      // Blue
    'sick': '#EF4444',        // Red
    'personal': '#10B981',    // Green
    'emergency': '#EF4444',   // Red
    'maternity': '#EC4899',   // Pink
    'paternity': '#6366F1',   // Indigo
    'casual': '#8B5CF6',      // Purple
    'vacation': '#06B6D4',    // Cyan
    'bereavement': '#6B7280', // Gray
    'holiday': '#84CC16',     // Lime
    
    // Database codes
    'al': '#3B82F6',      // Annual Leave - Blue
    'sl': '#EF4444',      // Sick Leave - Red
    'pl': '#10B981',      // Personal Leave - Green
    'el': '#EF4444',      // Emergency Leave - Red
    'ml': '#EC4899',      // Maternity Leave - Pink
    'ptl': '#6366F1',     // Paternity Leave - Indigo
    'cl': '#8B5CF6',      // Casual Leave - Purple
    'vl': '#06B6D4',      // Vacation Leave - Cyan
    'bl': '#6B7280',      // Bereavement Leave - Gray
    'hl': '#84CC16'       // Holiday Leave - Lime
  };
  
  return colors[normalizedType] || '#6B7280';
};

const formatLeaveTypeName = (leaveType) => {
  if (!leaveType) {
    return 'Leave';
  }
  
  // Handle numeric IDs (from your database structure)
  const leaveTypeById = {
    1: 'Annual Leave',
    2: 'Sick Leave', 
    3: 'Personal Leave',
    4: 'Maternity Leave',
    5: 'Emergency Leave',
    6: 'Paternity Leave',
    7: 'Casual Leave',
    11: 'Special Leave'
  };
  
  // Check if it's a numeric ID first
  if (typeof leaveType === 'number' || !isNaN(leaveType)) {
    return leaveTypeById[parseInt(leaveType)] || `Leave Type ${leaveType}`;
  }
  
  if (typeof leaveType !== 'string') {
    return 'Leave';
  }
  
  // Handle common database codes and formats
  const names = {
    // Standard leave types
    'annual': 'Annual Leave',
    'sick': 'Sick Leave', 
    'personal': 'Personal Leave',
    'emergency': 'Emergency Leave',
    'maternity': 'Maternity Leave',
    'paternity': 'Paternity Leave',
    'casual': 'Casual Leave',
    'special': 'Special Leave',
    
    // Database codes (common abbreviations)
    'AL': 'Annual Leave',
    'SL': 'Sick Leave',
    'PL': 'Personal Leave',
    'EL': 'Emergency Leave',
    'ML': 'Maternity Leave',
    'PTL': 'Paternity Leave',
    'CL': 'Casual Leave',
    'VL': 'Vacation Leave',
    'BL': 'Bereavement Leave',
    'HL': 'Holiday Leave',
    'SPL': 'Special Leave',
    
    // Lowercase versions
    'al': 'Annual Leave',
    'sl': 'Sick Leave',
    'pl': 'Personal Leave',
    'el': 'Emergency Leave',
    'ml': 'Maternity Leave',
    'ptl': 'Paternity Leave',
    'cl': 'Casual Leave',
    'vl': 'Vacation Leave',
    'bl': 'Bereavement Leave',
    'hl': 'Holiday Leave',
    'spl': 'Special Leave'
  };
  
  return names[leaveType] || leaveType.replace(/[_-]/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

const getUsagePercentage = (balance) => {
  const total = balance.total_days || balance.total || 1;
  const used = balance.used_days || balance.used || 0;
  return Math.round((used / total) * 100);
};

const getStatusBadgeClass = (status) => {
  const classes = {
    pending: 'ep-badge ep-badge-warning',
    approved: 'ep-badge ep-badge-success',
    rejected: 'ep-badge ep-badge-danger',
    cancelled: 'ep-badge ep-badge-neutral',
  };
  return classes[status] || classes.pending;
};

const formatStatus = (status) => {
  return status.charAt(0).toUpperCase() + status.slice(1);
};

const formatDateRange = (request) => {
  const startDate = formatDate(request.start_date);
  if (request.start_date === request.end_date) {
    return startDate;
  }
  return `${startDate} - ${formatDate(request.end_date)}`;
};

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const formatDateTime = (dateTimeString) => {
  if (!dateTimeString) return 'N/A';
  return new Date(dateTimeString).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatRelativeDate = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
  
  if (diffInDays === 0) return 'today';
  if (diffInDays === 1) return 'yesterday';
  if (diffInDays < 7) return `${diffInDays} days ago`;
  if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks ago`;
  return formatDate(dateString);
};

const quickRequest = (leaveType) => {
  preSelectedLeaveType.value = leaveType;
  showNewRequestModal.value = true;
};

const closeNewRequestModal = () => {
  showNewRequestModal.value = false;
  preSelectedLeaveType.value = null;
};

const fetchLeaveRequests = async () => {
  try {
    console.log('📋 Fetching leave requests for employee...');
    const response = await httpClient.get('/employee/leaves');
    console.log('📋 Leave requests API response:', response);
    console.log('📋 Response data:', response.data);
    
    let requests = response.data.data?.records || response.data?.records || response.data?.data || response.data || [];
    console.log('📋 Raw requests:', requests);
    
    // Transform requests to match expected format if needed
    if (Array.isArray(requests)) {
      requests = requests
        .filter(request => !isShortLeaveRecord(request))
        .map(request => ({
          ...request,
          // Ensure consistent field names
          leave_type: request.leave_type || request.type || 'unknown',
          start_date: request.start_date || request.startDate,
          end_date: request.end_date || request.endDate,
          created_at: request.created_at || request.createdAt,
          days: request.days || request.duration || 1
        }));
    }
    
    console.log('📋 Final processed requests:', requests);
    leaveRequests.value = requests;
    
    if (requests.length === 0) {
      console.log('ℹ️ No leave requests found for this employee');
    }
  } catch (error) {
    console.error('❌ Failed to fetch leave requests:', error);
    console.error('❌ Error details:', error.response?.data);
    showError(`Failed to load leave requests: ${error.response?.data?.message || error.message}`);
    leaveRequests.value = [];
  }
};

const fetchLeaveBalances = async () => {
  try {
    console.log('📊 Fetching leave balances for employee...');
    const response = await httpClient.get('/employee/leaves/balance');
    console.log('📊 Leave balances API response:', response);
    console.log('📊 Response data:', response.data);

    // Handle the new Balance Banks API structure
    let apiData = response.data.data || response.data || {};
    console.log('📊 Raw API data:', apiData);

    // Extract balance banks from the new API response structure
    let balanceBanks = apiData.balanceBanks || [];
    console.log('📊 Raw balance banks:', balanceBanks);

    // Transform Balance Banks data to display format
    let balances = [];

    if (Array.isArray(balanceBanks)) {
      balances = balanceBanks
        .filter(bank => !isShortLeaveCode(bank.code) && !/short\s*leave/i.test(bank.name || ''))
        .map(bank => {
        console.log('📊 Processing balance bank:', bank);

        // Format leave types in this bank for display
        const leaveTypesText = bank.leaveTypes && bank.leaveTypes.length > 0
          ? bank.leaveTypes.map(lt => lt.name).join(', ')
          : 'No leave types assigned';

        // Use bank color or default
        const bankColor = bank.color || '#3B82F6';

        // Create a processed balance card for the bank
        const processedBalance = {
          id: bank.bankId,
          leave_type: bank.code?.toLowerCase() || 'unknown',
          code: bank.code,
          name: bank.name || 'Unknown Bank',
          description: bank.description,
          color: bankColor,
          unit: 'days',
          total_days: Math.round(bank.allocated || 0),
          used_days: Math.round(bank.used || 0),
          pending_days: Math.round(bank.pending || 0),
          remaining_days: Math.round(bank.remaining || 0),
          carried_over: Math.round(bank.carriedOver || 0),
          bonus: Math.round(bank.bonus || 0),
          year: new Date().getFullYear(),
          is_bank: true,
          leave_types: bank.leaveTypes || [],
          leave_types_text: leaveTypesText,
          calculation_type: bank.calculationType,
          is_overridden: false
        };

        console.log('📊 Processed balance bank:', processedBalance);
        return processedBalance;
      });
    }

    console.log('📊 Final processed balances:', balances);

    if (balances.length === 0) {
      console.warn('⚠️ No leave balance banks found, check API endpoint and employee data');
      showError('No leave balance data found. Please contact your HR department.');
    }

    leaveBalances.value = balances;
  } catch (error) {
    console.error('❌ Failed to fetch leave balances:', error);
    console.error('❌ Error details:', error.response?.data);
    showError(`Failed to load leave balances: ${error.response?.data?.message || error.message}`);

    // Only use mock data in development
    if (import.meta.env.DEV) {
      console.log('🔧 Development mode: Using mock data');
      leaveBalances.value = getMockLeaveBalances();
    } else {
      leaveBalances.value = [];
    }
  }
};

// Mock data helper
const getMockLeaveBalances = () => [
  { 
    id: 1,
    leave_type: 'annual', 
    code: 'AL',
    name: 'Annual Leave',
    total_days: 25, 
    used_days: 8, 
    remaining_days: 17,
    unit: 'days'
  },
  { 
    id: 2,
    leave_type: 'sick', 
    code: 'SL',
    name: 'Sick Leave',
    total_days: 10, 
    used_days: 2, 
    remaining_days: 8,
    unit: 'days'
  },
  { 
    id: 3,
    leave_type: 'personal', 
    code: 'PL',
    name: 'Personal Leave',
    total_days: 5, 
    used_days: 1, 
    remaining_days: 4,
    unit: 'days'
  }
];

const fetchLeaveTypes = async () => {
  try {
    const response = await httpClient.get('/employee/leave-types');
    leaveTypes.value = response.data.data?.leaveTypes || response.data.data || response.data || [];
  } catch (error) {
    console.error('Failed to fetch leave types:', error);
  }
};

// Enhanced leave types that merge with balance data
const enhancedLeaveTypes = computed(() => {
  if (leaveTypes.value.length === 0) return [];

  return leaveTypes.value
    .map(type => {
    // Find corresponding balance data
    const balance = leaveBalances.value.find(b => 
      b.leave_type === type.code?.toLowerCase() || 
      b.name?.toLowerCase() === type.name?.toLowerCase() ||
      b.id === type.id
    );
    
    return {
      id: type.id,
      code: type.code,
      name: type.name,
      description: type.description,
      color: type.color,
      maxPerYear: type.maxPerYear,
      unit: type.unit || 'days',
      isActive: type.isActive,
      // Add balance information
      balance: balance?.remaining_days || balance?.remaining || 0,
      allocated: balance?.total_days || balance?.allocated || type.maxPerYear || 0,
      used: balance?.used_days || balance?.used || 0,
      remaining: balance?.remaining_days || balance?.remaining || 0
    };
  }).filter(type => type.isActive !== false); // Only show active leave types
});

const handleNewRequest = async (requestData) => {
  try {
    const response = await httpClient.post('/employee/leaves', requestData);
    closeNewRequestModal();
    showSuccess('Leave request submitted successfully');
    await fetchLeaveRequests();
    await fetchLeaveBalances();
  } catch (error) {
    showError(error.response?.data?.message || 'Failed to submit leave request');
  }
};

const viewRequest = (request) => {
  selectedRequest.value = request;
  showViewModal.value = true;
};

const closeViewModal = () => {
  showViewModal.value = false;
  selectedRequest.value = null;
};

const cancelRequest = async (request) => {
  if (!confirm('Are you sure you want to cancel this leave request?')) return;

  try {
    await httpClient.put(`/employee/leave-requests/${request.id}/cancel`, {});
    showSuccess('Leave request cancelled successfully');
    await fetchLeaveRequests();
    await fetchLeaveBalances();

    if (showViewModal.value) {
      closeViewModal();
    }
  } catch (error) {
    showError(error.response?.data?.message || 'Failed to cancel leave request');
  }
};

const canCancel = (request) => {
  return request.status === 'pending';
};

const applyFilters = () => {
  currentPage.value = 1;
};

const resetFilters = () => {
  Object.assign(filters, {
    status: '',
    type: '',
    dateRange: '',
  });
  currentPage.value = 1;
};

const previousPage = () => {
  if (currentPage.value > 1) currentPage.value--;
};

const nextPage = () => {
  if (currentPage.value < totalPages.value) currentPage.value++;
};

const goToPage = (page) => {
  if (typeof page === 'number') currentPage.value = page;
};

const showSuccess = (message) => {
  successMessage.value = message;
  setTimeout(() => {
    successMessage.value = '';
  }, 5000);
};

const showError = (message) => {
  errorMessage.value = message;
  setTimeout(() => {
    errorMessage.value = '';
  }, 5000);
};

// Lifecycle
onMounted(() => {
  fetchLeaveRequests();
  fetchLeaveBalances();
  fetchLeaveTypes();
});
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.toast-pop {
  animation: toastIn 0.25s ease-out;
}

@keyframes toastIn {
  from { transform: translateX(24px); opacity: 0; }
  to   { transform: translateX(0);    opacity: 1; }
}
</style>
